import useAuth from '@rhg/libs/auth0/use-auth';
import { UseQueryResult, skipToken, useQueries } from '@rhg/libs/react-query';
import { useCircle } from '../use-circle';
import { workflowByDefnIdForRecipientQueryOptions } from '../use-workflows';

interface UseWorkflowQueriesProps {
    hasFetchedCircleRecommendations: boolean;
    circleId: number;
    workflowDefnIds?: string[];
}
interface UseWorkflowQueriesResponse {
    workflowQueries?: UseQueryResult[];
    isFetched: boolean;
    isPending: boolean;
}

export function useWorkflowQueries({
    workflowDefnIds,
    circleId,
    hasFetchedCircleRecommendations,
}: UseWorkflowQueriesProps): UseWorkflowQueriesResponse {
    const { isAuthenticated, getAccessTokenSilently } = useAuth();
    const { circle } = useCircle(circleId);
    const circleAuthorizationToken = circle?.authorizationJWT;
    const canFetchWorkflows =
        !!workflowDefnIds?.length && hasFetchedCircleRecommendations;

    // We potentially could cut down on API calls by getting all workflows for the recipient at once if we want to optimize, here is a good place to look - see https://github.com/calm/evexia-web/pull/452#discussion_r1565880016
    // Returns first workflow for each program recommendation that has an ID
    const workflowQueries = useQueries({
        queries: canFetchWorkflows
            ? workflowDefnIds.map((workflowDefnId) => ({
                  queryKey: workflowByDefnIdForRecipientQueryOptions.queryKey({
                      workflowDefnId,
                      recipientId: circleId,
                  }),
                  queryFn: () =>
                      circleAuthorizationToken
                          ? workflowByDefnIdForRecipientQueryOptions.queryFn(
                                getAccessTokenSilently,
                                circleAuthorizationToken,
                                circleId,
                                workflowDefnId
                            )
                          : skipToken,
                  ...workflowByDefnIdForRecipientQueryOptions.queryOptions(
                      isAuthenticated,
                      circleId,
                      circleAuthorizationToken
                  ),
              }))
            : [],
    });

    const allFetched = workflowQueries.every((query) => query.isSuccess);

    return {
        workflowQueries,
        isFetched: (canFetchWorkflows && allFetched) || !canFetchWorkflows,
        isPending: workflowQueries.some((query) => query.isPending),
    };
}
